/**
 * サブスクリプション・使用量制限 API サービス
 */

import { PLAN_NAME, PLAN_TYPE, type PlanType } from '@/constants/planTypes';
import { throwIfRateLimited } from '@/services/apiErrors';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// プランの識別子と表示名の SSOT は constants/planTypes.ts。
// 以前からこのモジュール経由で参照している箇所があるため再エクスポートする
export type { PlanType };

/**
 * プラン制限
 */
export interface PlanLimits {
  maxActiveEvents: number;
  aiGenerationsPerMonth: number;
  maxParticipantsPerEvent: number;
  pollQuestionsPerEvent: number;
  pollPeriodsPerEvent: number;
  allowGuests: boolean;
  allowOgShare: boolean;
  allowPageDesign: boolean;
  allowLineNotification: boolean;
  maxCollaboratorsPerEvent: number;
  urlImportsPerDay: number;
  aiUrlImportsPerDay: number;
  allowAiUrlImport: boolean;
  /** AIチャットの月間予算（マイクロドル。トークン数を単価で重み付けした量） */
  chatBudgetMicroUsdPerMonth: number;
}

/**
 * 使用量カウント
 */
export interface UsageCount {
  eventsCreated: number;
  aiGenerations: number;
  urlImportsToday: number;
  aiUrlImportsToday: number;
}

/**
 * 残り使用量
 */
export interface UsageRemaining {
  activeEvents: number;
  aiGenerationsPerMonth: number;
  urlImportsToday: number;
  aiUrlImportsToday: number;
}

/**
 * 使用量サマリー
 */
export interface UsageSummary {
  planType: PlanType;
  billingInterval: 'monthly' | 'yearly' | null;
  /** 契約期間の終了日（ISO 8601）。無料プランでは null */
  currentPeriodEnd: string | null;
  limits: PlanLimits;
  usage: UsageCount;
  remaining: UsageRemaining;
}

/**
 * 制限超過エラー
 */
export interface LimitExceededError {
  error: 'LIMIT_EXCEEDED';
  message: string;
  details: {
    limitType: string;
    currentUsage: number;
    limitValue: number;
  };
}

/**
 * 機能利用不可エラー
 */
export interface FeatureNotAvailableError {
  error: 'FEATURE_NOT_AVAILABLE';
  message: string;
}

export class SubscriptionError extends Error {
  readonly status: number;
  readonly limitDetails?: LimitExceededError['details'];

  constructor(message: string, status: number, limitDetails?: LimitExceededError['details']) {
    super(message);
    this.name = 'SubscriptionError';
    this.status = status;
    this.limitDetails = limitDetails;
  }
}

/**
 * サブスクリプション情報と使用量を取得
 * @param token - Cognito JWT トークン
 */
export const getSubscription = async (token: string): Promise<UsageSummary> => {
  const response = await fetch(`${API_BASE_URL}/api/user/subscription`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
      throwIfRateLimited(response);
    if (response.status === 401) {
      throw new SubscriptionError('認証が必要です', 401);
    }
    const error = await response
      .json()
      .catch(() => ({ message: 'サブスクリプション情報の取得に失敗しました' }));
    throw new SubscriptionError(
      error.message || 'サブスクリプション情報の取得に失敗しました',
      response.status
    );
  }

  return response.json();
};

/**
 * レスポンスが制限超過エラーかどうかを判定
 */
export const isLimitExceededError = (error: unknown): error is { response: LimitExceededError } => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response: unknown }).response;
    return (
      response !== null &&
      typeof response === 'object' &&
      'error' in response &&
      (response as { error: string }).error === 'LIMIT_EXCEEDED'
    );
  }
  return false;
};

/**
 * プラン名の日本語表示
 */
export const getPlanDisplayName = (planType: PlanType): string =>
  PLAN_NAME[planType] ?? planType;

/**
 * 制限値の表示（-1の場合は「無制限」）
 */
export const formatLimitValue = (value: number): string => {
  return value === -1 ? '無制限' : `${value}`;
};

/**
 * フロントエンド用のプラン制限デフォルト値
 * バックエンドから取得できない場合に使用
 */
export const DEFAULT_PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  free: {
    maxActiveEvents: 3,
    aiGenerationsPerMonth: 5,
    maxParticipantsPerEvent: 10,
    pollQuestionsPerEvent: 30,
    pollPeriodsPerEvent: 30,
    allowGuests: true,
    allowOgShare: true,
    allowPageDesign: false,
    allowLineNotification: false,
    maxCollaboratorsPerEvent: 1,
    urlImportsPerDay: 10,
    aiUrlImportsPerDay: 0,
    allowAiUrlImport: false,
    chatBudgetMicroUsdPerMonth: 20_000,
  },
  basic: {
    maxActiveEvents: 100,
    aiGenerationsPerMonth: 35,
    maxParticipantsPerEvent: 50,
    pollQuestionsPerEvent: 30,
    pollPeriodsPerEvent: 30,
    allowGuests: true,
    allowOgShare: true,
    allowPageDesign: true,
    allowLineNotification: false,
    maxCollaboratorsPerEvent: 5,
    urlImportsPerDay: 30,
    aiUrlImportsPerDay: 10,
    allowAiUrlImport: true,
    chatBudgetMicroUsdPerMonth: 150_000,
  },
  premium: {
    maxActiveEvents: 10000,
    aiGenerationsPerMonth: 100,
    maxParticipantsPerEvent: 200,
    pollQuestionsPerEvent: 30,
    pollPeriodsPerEvent: 30,
    allowGuests: true,
    allowOgShare: true,
    allowPageDesign: true,
    allowLineNotification: true,
    maxCollaboratorsPerEvent: 20,
    urlImportsPerDay: 100,
    aiUrlImportsPerDay: 30,
    allowAiUrlImport: true,
    chatBudgetMicroUsdPerMonth: 500_000,
  },
};

/**
 * プラン情報取得エラー時のデフォルト値（無料プランとして扱う）
 * 使用量は0として扱い、バックエンドで最終的なチェックを行う
 */
export const DEFAULT_FREE_USAGE_SUMMARY: UsageSummary = {
  planType: PLAN_TYPE.FREE,
  billingInterval: null,
  currentPeriodEnd: null,
  limits: DEFAULT_PLAN_LIMITS.free,
  usage: {
    eventsCreated: 0,
    aiGenerations: 0,
    urlImportsToday: 0,
    aiUrlImportsToday: 0,
  },
  remaining: {
    activeEvents: DEFAULT_PLAN_LIMITS.free.maxActiveEvents,
    aiGenerationsPerMonth: DEFAULT_PLAN_LIMITS.free.aiGenerationsPerMonth,
    urlImportsToday: DEFAULT_PLAN_LIMITS.free.urlImportsPerDay,
    aiUrlImportsToday: DEFAULT_PLAN_LIMITS.free.aiUrlImportsPerDay,
  },
};

/**
 * Stripe Price ID 定義
 */
export const STRIPE_PRICES = {
  basic: {
    monthly: 'price_1T9FSMLVDWBhUIG7WGX1Z4Rb',
    yearly: 'price_1T9FTCLVDWBhUIG71RxIAPrO',
  },
  premium: {
    monthly: 'price_1T9FVeLVDWBhUIG7UDw4C1kP',
    yearly: 'price_1T9FVeLVDWBhUIG78Qn1XB4E',
  },
} as const;

/**
 * Checkout APIレスポンス
 * - 新規サブスク: { url: "https://checkout.stripe.com/..." }
 * - プレビュー: { preview: true, immediateAmount, nextRecurringAmount, ... }
 * - プラン変更確定: { updated: true, planType: "premium" }
 */
export type CheckoutResponse =
  | { url: string; preview?: never; updated?: never }
  | {
      preview: true;
      immediateAmount: number;
      nextRecurringAmount: number;
      nextChargeDate: string | null;
      currency: string;
      planType: string;
      minutesUntilAmountChange: number | null;
      prorationDate: number;
      creditBalance: number;
      url?: never;
      updated?: never;
    }
  | { updated: true; planType: string; url?: never; preview?: never };

/**
 * Stripe Checkout セッションを作成、プレビュー、またはプラン変更を確定
 */
export const createCheckoutSession = async (
  token: string,
  priceId: string,
  options?: { confirm?: boolean; prorationDate?: number }
): Promise<CheckoutResponse> => {
  const body: Record<string, unknown> = { priceId };
  if (options?.confirm) {
    body.confirm = true;
  }
  if (options?.prorationDate) {
    body.prorationDate = options.prorationDate;
  }

  const response = await fetch(`${API_BASE_URL}/api/subscription/checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
      throwIfRateLimited(response);
    const error = await response.json().catch(() => ({ error: '決済セッションの作成に失敗しました' }));
    throw new SubscriptionError(
      error.error || '決済セッションの作成に失敗しました',
      response.status
    );
  }

  return response.json();
};

/**
 * Stripe Customer Portal セッションを作成
 */
export const createPortalSession = async (token: string): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/api/subscription/portal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
      throwIfRateLimited(response);
    const error = await response.json().catch(() => ({ error: 'ポータルセッションの作成に失敗しました' }));
    throw new SubscriptionError(
      error.error || 'ポータルセッションの作成に失敗しました',
      response.status
    );
  }

  const data = await response.json();
  return data.url;
};
